import { useContext, useState } from 'react';
import { SmartContext } from '../../../library/Core/SmartContext';
import { getDomainData, getDomainValueForCode, isEmpty } from '../../../library/Core/SmartFunctions';
import OutreachDashboardService from './OutreachDashboardService';
import AsyncSelect from 'react-select/async';
import SummaryStatsPanel from './components/SummaryStatsPanel';
import DispositionBreakdown from './components/DispositionBreakdown';
import PurposeOfOutreach from './components/PurposeOfOutreach';
import TopModes from './components/TopModes';
import OutreachByTeamChart from './components/OutreachByTeamChart';
import TopMembersTeamTable from './components/TopMembersTeamTable';
import TopMembersAlumniTable from './components/TopMembersAlumniTable';
import { Link } from 'react-router-dom';

const OutreachDashboardLayout = () => {
    const { state, dispatch } = useContext(SmartContext);

    const [selectedGroup, setSelectedGroup] = useState<any>(null);
    const selectedInstitute = state.data.selectedOutreachDashboardInstitue ?? '';

    const loadGroupOptions = async (inputValue: string) => {
        if (inputValue.length < 3) return [];
        const domainData = await getDomainData();
        const schoolGroups = domainData.filter((d: any) => d.categoryCode === 'SCHOOL_GROUP');

        return schoolGroups
            .map((g: any) => ({ value: g.code, label: g.value }))
            .filter((option: any) => option.label.toUpperCase().includes(inputValue.toUpperCase()));
    };

    const loadInstituteOptions = async (inputValue: string) => {
        if (inputValue.length < 3) return [];
        const domainData = await getDomainData();
        const instituteDomains = domainData.filter((d: any) => d.categoryCode === 'INSTITUTE_DOMAIN');

        // If a group is selected, filter institutes by that group; otherwise show all
        const filtered = selectedGroup
            ? instituteDomains.filter((inst: any) => inst.parentCode === selectedGroup.value)
            : instituteDomains;

        return filtered
            .map((inst: any) => ({ value: inst.code, label: inst.value }))
            .filter((option: any) => option.label.toUpperCase().includes(inputValue.toUpperCase()));
    };

    const handleGroupChange = (selectedOption: any) => {
        setSelectedGroup(selectedOption);
        // Reset institute selection when group changes
        dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: { dataKey: 'selectedOutreachDashboardInstitue', value: '' },
        });
    };

    const handleInstituteChange = (selectedOption: any) => {
        if (!selectedOption) {
            dispatch({
                type: 'REFRESH_DATA',
                payload: {
                    data: {
                        ...state.data,
                        selectedOutreachDashboardInstitue: '',
                        summary: {},
                        dispositions: [],
                        outreachByTeam: { labels: [], data: [] },
                        purposeOfOutreach: [],
                        topModes: [],
                        topMembersTeam: [],
                        topMembersAlumni: [],
                    },
                },
            });
            return;
        }
        const newInstituteId = selectedOption.value;
        dispatch({
            type: 'CONTROL_VALUE_CHANGE',
            payload: { dataKey: 'selectedOutreachDashboardInstitue', value: newInstituteId },
        });

        OutreachDashboardService.getSummary(newInstituteId)
            .then((res: any) => {
                const summaryData = res?.data ?? {};
                dispatch({
                    type: 'REFRESH_DATA',
                    payload: {
                        data: {
                            ...state.data,
                            selectedOutreachDashboardInstitue: newInstituteId,
                            summary: summaryData?.summary,
                            dispositions: summaryData?.dispositions,
                            outreachByTeam: summaryData?.outreachByTeam,
                            purposeOfOutreach: summaryData?.purposeOfOutreach,
                            topModes: summaryData?.topModes,
                            topMembersTeam: summaryData?.topMembersTeam,
                            topMembersAlumni: summaryData?.topMembersAlumni
                        },
                    },
                });
            })
            .catch((err: any) => console.log(err));
    };

    return (
        <div className="main flex-1">
            <section className="alumni_directory_details container-fluid">
                <div className="max-1140 pt-3 pt-sm-5 px-lg-0 pb-5 mx-auto">
                    <div className="row g-4">
                        <div className="col-12">
                            <div className="row g-2 align-items-center">
                                <div className="col">
                                    <h2 className="font-26 font-600 mb-0">Institute Summary</h2>
                                    
                                    <div className="d-flex gap-2 mt-1" style={{ maxWidth: 640 }}>
                                        <div style={{ minWidth: 300, flex: 1 }}>
                                            <AsyncSelect
                                                cacheOptions
                                                loadOptions={loadGroupOptions}
                                                onChange={handleGroupChange}
                                                value={selectedGroup}
                                                placeholder="Select Institute Group"
                                                isClearable
                                            />
                                        </div>
                                        <div style={{ minWidth: 300, flex: 1 }}>
                                            <AsyncSelect
                                                key={selectedGroup?.value ?? 'all'}
                                                cacheOptions
                                                loadOptions={loadInstituteOptions}
                                                onChange={handleInstituteChange}
                                                value={
                                                    selectedInstitute
                                                        ? {
                                                              value: selectedInstitute,
                                                              label:
                                                                  getDomainValueForCode(String(selectedInstitute), 'INSTITUTE_DOMAIN', state) ||
                                                                  selectedInstitute,
                                                          }
                                                        : null
                                                }
                                                placeholder="Select Institute"
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className="col-auto text-sm-end">
                                    <Link
                                        to="/upload-outreach"
                                        className="btn btn-white radius-6 custom-shadow me-1 text-decoration-none px-2">
                                        <i className="bi bi-cloud-arrow-up font-18 position-relative"></i>{' '}
                                        <span className="line-height-16">Outreach Bulk Upload</span>
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Summary Stats */}
                        {!isEmpty(state?.data?.summary) ? <SummaryStatsPanel /> : (
                            <div className="col-12">
                                <div className="bg-white p-3 custom-shadow h-100 radius-6">
                                    <p className="font-14 text-gray-64">No summary data available.</p>
                                </div>
                            </div>
                        )}

                        {/* Disposition Breakdown */}
                        <DispositionBreakdown />

                        {/* Outreach by Team */}
                        <div className="col-md-6">
                            <OutreachByTeamChart />
                        </div>

                        {/* Purpose of Outreach */}
                        <div className="col-md-6">
                            <PurposeOfOutreach />
                        </div>

                        {/* Top Modes */}
                        <div className="col-md-6">
                            <TopModes />
                        </div>

                        {/* Top Members (Team) */}
                        <div className="col-md-6">
                            <TopMembersTeamTable />
                        </div>

                        {/* Top Members (Alumni) */}
                        <div className="col-md-12">
                            <TopMembersAlumniTable />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default OutreachDashboardLayout;
